import type { InstalledPackage } from '@/core/types/introspection.js';

export interface OsvVulnerabilityRecord {
  package_name: string;
  ecosystem: string;
  version: string;
  advisory_id: string;
  summary: string;
  details: string;
}

function toOsvEcosystem(ecosystem: InstalledPackage['ecosystem']): string | null {
  if (ecosystem === 'node') {
    return 'npm';
  }

  if (ecosystem === 'php') {
    return 'Packagist';
  }

  if (ecosystem === 'dart') {
    return 'Pub';
  }

  return null;
}

export async function queryOsv(packages: InstalledPackage[]): Promise<OsvVulnerabilityRecord[]> {
  const queries = packages
    .map((pkg) => ({
      pkg,
      ecosystem: toOsvEcosystem(pkg.ecosystem),
    }))
    .filter(
      (entry): entry is { pkg: InstalledPackage; ecosystem: string } => entry.ecosystem !== null,
    )
    .map((entry) => ({
      package: {
        name: entry.pkg.name,
        ecosystem: entry.ecosystem,
      },
      version: entry.pkg.locked_version,
    }));

  if (queries.length === 0) {
    return [];
  }

  try {
    const response = await fetch('https://api.osv.dev/v1/querybatch', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ queries }),
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      return [];
    }

    const payload = (await response.json()) as {
      results?: Array<{
        vulns?: Array<{
          id?: string;
          summary?: string;
          details?: string;
        }>;
      }>;
    };

    return (payload.results ?? []).flatMap((result, index) =>
      (result.vulns ?? []).map((vuln) => ({
        package_name: queries[index]?.package.name ?? 'unknown',
        ecosystem: queries[index]?.package.ecosystem ?? 'unknown',
        version: queries[index]?.version ?? 'unknown',
        advisory_id: vuln.id ?? 'unknown',
        summary: vuln.summary ?? 'OSV advisory',
        details: vuln.details ?? '',
      })),
    );
  } catch {
    return [];
  }
}
